import { useEffect, useRef, useState } from "react";
import { geocodeApi, type GeocodeResult } from "@/api/geocode";
import { FloatingCard } from "@/components/primitives/FloatingCard";
import { SearchField } from "@/components/primitives/SearchField";
import { cn } from "@/lib/cn";
import type { ViewportTarget } from "./DrawToolbar";

type Props = {
	onGoToLocation: (target: ViewportTarget) => void;
	className?: string;
};

export function LocationSearch({ onGoToLocation, className }: Props) {
	const [query, setQuery] = useState("");
	const [results, setResults] = useState<GeocodeResult[]>([]);
	const [searching, setSearching] = useState(false);
	const [searchError, setSearchError] = useState<string | null>(null);
	const [open, setOpen] = useState(false);
	const wrapRef = useRef<HTMLDivElement | null>(null);
	const debounceRef = useRef<number | null>(null);
	const reqIdRef = useRef(0);

	useEffect(() => {
		const onDoc = (e: MouseEvent) => {
			if (!wrapRef.current?.contains(e.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", onDoc);
		return () => document.removeEventListener("mousedown", onDoc);
	}, []);

	useEffect(() => {
		if (debounceRef.current) window.clearTimeout(debounceRef.current);
		const q = query.trim();
		if (q.length < 2) {
			setResults([]);
			setSearchError(null);
			setSearching(false);
			return;
		}

		setSearching(true);
		debounceRef.current = window.setTimeout(() => {
			const id = ++reqIdRef.current;
			void geocodeApi
				.search(q)
				.then((res) => {
					if (id !== reqIdRef.current) return;
					setResults(res.results);
					setSearchError(res.results.length === 0 ? "Kein Treffer" : null);
					setOpen(true);
				})
				.catch((e) => {
					if (id !== reqIdRef.current) return;
					setResults([]);
					setSearchError(e instanceof Error ? e.message : "Suche fehlgeschlagen");
					setOpen(true);
				})
				.finally(() => {
					if (id === reqIdRef.current) setSearching(false);
				});
		}, 350);

		return () => {
			if (debounceRef.current) window.clearTimeout(debounceRef.current);
		};
	}, [query]);

	const pickResult = (item: GeocodeResult) => {
		onGoToLocation({
			center: item.center,
			bbox: item.bbox,
			zoom: item.bbox ? undefined : 14,
		});
		setQuery(item.label.split(",")[0] ?? item.label);
		setOpen(false);
		setResults([]);
	};

	const submitSearch = () => {
		const first = results[0];
		if (first) {
			pickResult(first);
			return;
		}
		const q = query.trim();
		if (q.length < 2) return;
		setSearching(true);
		void geocodeApi
			.search(q)
			.then((res) => {
				if (res.results[0]) pickResult(res.results[0]);
				else setSearchError("Kein Treffer");
			})
			.catch((e) => {
				setSearchError(e instanceof Error ? e.message : "Suche fehlgeschlagen");
			})
			.finally(() => setSearching(false));
	};

	return (
		<div ref={wrapRef} className={cn("relative min-w-0", className)}>
			<SearchField
				value={query}
				onChange={setQuery}
				onSubmit={submitSearch}
				placeholder="Ort suchen…"
				searching={searching}
				inputProps={{
					"aria-label": "Ort suchen",
					onFocus: () => {
						if (results.length > 0 || searchError) setOpen(true);
					},
				}}
			/>
			{open && (results.length > 0 || searchError) ? (
				<FloatingCard className="absolute left-0 right-0 top-full z-30 mt-1 max-h-56 overflow-y-auto py-1">
					<ul>
						{searchError && results.length === 0 ? (
							<li className="px-3 py-2 text-sm text-fg-muted">{searchError}</li>
						) : null}
						{results.map((item) => (
							<li key={item.id}>
								<button
									type="button"
									className="w-full truncate px-3 py-2 text-left text-sm hover:bg-surface-raised"
									onClick={() => pickResult(item)}
								>
									{item.label}
								</button>
							</li>
						))}
					</ul>
				</FloatingCard>
			) : null}
		</div>
	);
}
